import { isNil } from 'lodash';
import { MBAuthUtil } from '@shared/scenes/auth/util';
import { ASK_AUTH_BASE_URL } from './config';
import { PromptTokenResponse } from './types';

/**
 * Prompt tokens are short-lived (15 min). We cache the minted token per
 * businessId in-memory and re-mint when it is within this many ms of expiry so a
 * multi-turn session never streams with an expired token.
 */
const EXPIRY_SAFETY_MARGIN_MS = 30_000;

interface CachedToken {
  accessToken: string;
  expiresAt: number;
}

const tokenCache = new Map<number, CachedToken>();

/** Clears cached prompt tokens (e.g. after a 401/403 forces a re-mint). */
export function clearPromptTokenCache(businessId?: number): void {
  if (isNil(businessId)) {
    tokenCache.clear();
    return;
  }
  tokenCache.delete(businessId);
}

/**
 * Exchanges the logged-in user's Coast JWT for a short-lived prompt token scoped
 * to `businessId`. Returns a cached token when one is still valid unless
 * `forceRefresh` is set.
 */
export async function mintPromptToken(
  businessId: number,
  { forceRefresh = false }: { forceRefresh?: boolean } = {},
): Promise<string> {
  const cached = tokenCache.get(businessId);
  if (!forceRefresh && !isNil(cached) && cached.expiresAt > Date.now()) {
    return cached.accessToken;
  }

  const authToken = MBAuthUtil.getAuthToken();
  if (isNil(authToken)) {
    throw new Error('Cannot mint a prompt token without a Coast auth token');
  }

  const response = await fetch(`${ASK_AUTH_BASE_URL}/prompt-token`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ businessId }),
  });

  if (!response.ok) {
    throw new Error(`Failed to mint prompt token (status ${response.status})`);
  }

  const { accessToken, expiresIn }: PromptTokenResponse = await response.json();
  tokenCache.set(businessId, {
    accessToken,
    expiresAt: Date.now() + expiresIn * 1000 - EXPIRY_SAFETY_MARGIN_MS,
  });

  return accessToken;
}
